<template>
  <div>
    <v-progress-linear
      v-if="loading"
      indeterminate
    />
    <div v-else>
      <VTableRemote
        :iconHeader="hasPermission('clients.team.add') ? 'add' : null"
        :items="displayedMembers"
        :isSelectable="hasPermission('clients.members.view')"
        :pagination="pagination"
        :headers="tableHeaders"
        :title="$t('team.clientTeamMembers')"
        :isSearchable="false"
        :itemActions="actions"
        :isCustomIcon="true"
        :isInfinitScroll="true"
        :centerColumns="['actions']"
        :notSortableColumns="['actions', 'memberSince']"
        :sortableColumns="['firstname', 'lastname', 'email']"
        startIconName="person_outline"
        hideDefaultFooter
        @sendQueryParams="fetchQuery"
        @iconAction="onClickAction"
      >
        <template #SearchWithFilters>
          <SearchWithFilters
            :withoutFilter="true"
            :searchPlaceholder="$t('clients.searchBy')"
            :filterName="filterName"
            @onSearch="handleSearch"
          />
        </template>
        <template #item.emailInvitation="{ item }">
          <div class="table-cell-email-invitation">
            <span>{{ item.emailInvitation.email }}</span>
            <a
              v-if="item.emailInvitation.invited"
              @click.stop="onClickResendInvitation(item)"
            >
              {{ item.emailInvitation.label }}
            </a>
          </div>
        </template>
        <template #withInfiniteScroll>
          <div v-intersect="endIntersect" />
        </template>
      </VTableRemote>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import VTableRemote from '@/components/shared/content-views/VTableRemote';
import InviteForm from '@/components/invite/TheInviteForm';
import ClientTeamForm from '@/views/clients/ClientTeamForm.vue';
import clientRoles from '@/constants/clientRoles';
import SearchWithFilters from '@/components/shared/SearchWithFilters.vue';
import perpage from '@/constants/pagination';
import TeamMembersModal from './modalTeamMembers/TeamMembersModal.vue';

export default {
    components: {
        VTableRemote, SearchWithFilters,

    },
    data() {
        return {
            members: [],
            team: null,
            loading: false,
            filterName: 'clientsClientTeam',
            queryParams: { perpage, onlyExplicit: true, role: clientRoles.OPERATOR, includeInvited: true },
            pagination: {},
            initializingDatas: false,
        };
    },
    computed: {
        ...mapGetters([
            'client',
            'teamID',
            'hasPermission',
        ]),
        roles() {
            return Object.values(clientRoles).map(role => ({
                name: this.$t(`roles.${role}`),
                value: role,
            }));
        },
        tableHeaders() {
            return [
                {
                    text: '',
                    value: 'icon',
                    sortable: false,
                },
                {
                    text: this.$t('members.labels.firstName'),
                    value: 'firstName',
                    sortable: true,
                },
                {
                    text: this.$t('members.labels.lastName'),
                    value: 'lastName',
                },
                {
                    text: this.$t('members.labels.email'),
                    value: 'emailInvitation',
                    sort: (a, b) => a.email.localeCompare(b.email),
                },
                {
                    text: this.$t('members.labels.memberSince'),
                    value: 'memberSince',
                },
            ];
        },
        displayedMembers() {
            return this.members.map(({
                id,
                firstName,
                lastName,
                email,
                status,
                memberSince,
            }) => ({
                id,
                firstName,
                lastName,
                memberSince: this.$options.filters.formatDate(memberSince, 'l'),
                emailInvitation: {
                    invited: status === 'invited',
                    label: `(${this.$t('forms.labels.resendInvitation')})`,
                    email,
                },
            }));
        },
        actions() {
            const actions = [
                {
                    name: this.$t('forms.btn.edit'),
                    icon: 'edit',
                    mode: 'edit',
                },
                {
                    name: this.$t('forms.labels.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                    customValidationProperty: 'nbAnalysis',
                    showOncustomValidationProperty: false,
                },
            ];
            return actions;
        },
    },
    async created() {
        await this.getSites();
        await this.getClientOperatorMembers();
    },
    methods: {
        ...mapActions({
            resetMember: 'resetMemberInfo',
        }),
        async fetchQuery(queryParams) {
            if (!this.initializingDatas) {
                this.queryParams = {
                    ...this.queryParams,
                    ...queryParams,
                    page: 1,
                    order: this.preparOrderQueryParams(queryParams.order),
                };
                this.pagination = {};
                this.members = [];
                await this.getClientOperatorMembers();
            }
        },
        preparOrderQueryParams(value) {
            if (typeof value !== 'string') {
                return null;
            }

            const [name, order] = value.split('_');
            let newValue;
            switch (name) {
                case 'firstname':
                    newValue = 'firstName';
                    break;
                case 'lastname':
                    newValue = 'lastName';
                    break;
                case 'emailinvitation':
                    newValue = 'email';
                    break;

                default:
                    return null;
            }
            return `${newValue}_${order}`;
        },
        async getSites() {
            this.loading = true;
            const res = await this.$rq.getClientSites(this.client.id);
            this.sites = res.data.sites;
            this.loading = false;
        },
        async getClientOperatorMembers() {
            this.initializingDatas = true;
            const { data } = await this.$rq.getClientMembers(this.client.id, this.queryParams);
            this.pagination = data.pagination;
            this.members = [...this.members, ...data.members];
            this.initializingDatas = false;
        },
        async addClientTeam(requestPayload) {
            const { data } = await this.$rq.addClientTeam(this.client.id, requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('msg.clientTeamCreated', { data: requestPayload.name }),
            });
            this.team = data;
        },
        onClickUpdateClientTeam() {
            this.$root.$emit('showModal', {
                title: this.$t('team.editClientTeam'),
                component: Vue.component('clientTeamForm', ClientTeamForm),
                payload: {
                    populateWith: {
                        name: this.team.name,
                        active: this.team.active,
                    },
                },
                onConfirm: requestPayload => this.updateClientTeam(requestPayload),
            });
        },
        async updateClientTeam(requestPayload) {
            const { data } = await this.$rq.updateClientTeam(this.client.id, requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('msg.clientTeamModified', { data: data.name }),
            });
            this.team = data;
        },
        onClickAddClientTeamMember() {
            this.$root.$emit('showModal', {
                width: 750,
                title: this.$t('teams.addMember'),
                component: Vue.component('inviteForm', InviteForm),
                payload: {
                    sites: this.sites,
                    roles: this.roles,
                    client: this.client.id,
                },
                onConfirm: requestPayload => this.addClientTeamMember(requestPayload),
            });
        },
        async addClientTeamMember(requestPayload) {
            await this.$rq.postInviteUser(this.teamID, requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('msg.addedMember', {
                    user: `${requestPayload.firstName} ${requestPayload.lastName}`,
                }),
            });
            this.fetchQuery({});
        },
        onClickRedirect(member) {
            this.$router.push({
                name: 'clientTeamMember',
                params: {
                    memberId: member.id,
                },
            });
        },
        async onClickResendInvitation(member) {
            await this.$rq.resendInvitation(this.teamID, { memberId: member.id });
            this.$toasted.global.toast_success({
                message: this.$t('msg.invitationResent'),
            });
        },
        handleSearch(search) {
            this.queryParams = {
                ...this.queryParams,
                search,
            };
            this.fetchQuery({});
        },
        onClickAction(item, action) {
            if (item === 'add') {
                this.onClickAddClientTeamMember();
            }
            if (action) {
                if (action.mode === 'edit') {
                    this.openModal(item);
                }
                if (action.mode === 'delete') {
                    this.confirmDelete(item);
                }
            }
        },
        openModal(item) {
            this.$root.$emit('showModal', {
                width: 750,
                title: this.$t('members.memberInfo'),
                component: Vue.component('TeamMembersModal', TeamMembersModal),
                payload: {
                    memberId: item.id,
                    isEdit: true,
                },
                onConfirm: requestPayload => this.onClickUpdate(requestPayload),
            });
        },
        async confirmDelete(item) {
            const { firstName, lastName, id } = item;
            const { clientId } = this.$route.params;
            if (this.fromSection === 'clientTeam') {
                await this.$rq.deleteClientMember(clientId, id);
            } else {
                await this.$rq.removeMemberFromTeam(id);
            }
            this.resetMember();
            this.$toasted.global.toast_success({
                message: this.$t('msg.memberRemoved', {
                    data: `${firstName} ${lastName}`,
                }),
            });

            if (this.isMemberMyself) {
                await this.refreshUserTeams();
                await this.resetAccessControlState();
                await this.$router.push({ name: this.defaultUserRoute });
            } else {
                this.$router.go(-1);
            }
        },
        async onClickUpdate(updatedData) {
            const data = {
                preferences: {},
                local: {
                    email: updatedData.email,
                },
                profile: {
                    firstName: updatedData.firstName,
                    lastName: updatedData.lastName,
                    phoneNumber: updatedData.phoneNumber,
                },
            };
            await this.$rq.updateProfile(updatedData.user, data);
            this.fetchQuery({});
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition && !this.loading) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getClientOperatorMembers();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
    },
};
</script>

<style lang="scss" scoped>
    .createTeamButton {
        margin-top: 50px;
    }
</style>
